import React, { useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import { NotificationContext } from './context';
import * as Controller from './controller';

export default function useNotification() {
  const {
    unread,
    setUnread,
    loading,
    setLoading,
    notifications,
    setNotifications,
    pagination,
    setPagination,
  } = useContext(NotificationContext);

  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const fetchNotifications = useCallback(
    async (page) => {
      try {
        setLoading(true);

        const { data, pagination: receivedPagination } = await Controller.fetchNotifications(page);

        setPagination(receivedPagination);
        if (receivedPagination.page === 1) {
          setNotifications(data);
        } else {
          setNotifications((previousNotifications) => [...previousNotifications, ...data]);
        }
      } catch (ex) {
        // Handle exception
      } finally {
        setLoading(false);
      }
    },
    [setNotifications],
  );

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      const data = await Controller.fetchUnreadNotifications();
      const numericValue =
        typeof data === 'string' && data.trim() !== '' ? Number(data) : NaN;

      const hasUnread = Array.isArray(data)
        ? data.length > 0
        : typeof data === 'number'
          ? data > 0
          : typeof data === 'boolean'
            ? data
            : Number.isFinite(numericValue)
              ? numericValue > 0
              : !!(
                  data?.count > 0 ||
                  data?.total > 0 ||
                  data?.unread > 0 ||
                  data?.data?.length > 0
                );

      setUnread(hasUnread);
    } catch (ex) {
      // Handle exception
    }
  }, []);

  const updateNotification = useCallback(
    (notification) => {
      setNotifications(notifications.map((n) => (n.id === notification.id ? notification : n)));
    },
    [notifications],
  );

  const handleNotificationClick = useCallback(
    async (notification) => {
      const payload = JSON.parse(notification.url);
      if (payload && payload.url) {
        navigate(`/${payload.url}`);
      } else if (notification.type !== 'APP_NOTIFICATION') {
        snackbar.show(<Text>Notificação sem link</Text>, {
          type: 'error',
        });
      }

      if (!notification.read) {
        try {
          const updatedNotification = await Controller.markNotificationAsRead(notification);

          updateNotification(updatedNotification);
          fetchUnreadNotifications();
        } catch (ex) {
          // Handle exception
        }
      }
    },
    [navigate, updateNotification, fetchUnreadNotifications],
  );

  return {
    unread,
    loading,
    notifications,
    pagination,
    fetchNotifications,
    fetchUnreadNotifications,
    handleNotificationClick,
  };
}
